// ---------------------------------------------------------------------------
// Device storage: how much the app is using, and whether the browser has
// promised to keep it (PLAN 12 phase 5).
//
// This matters more than a number on a screen. Everything a canvasser records
// lives in IndexedDB until it syncs, and by default a browser may evict the
// whole origin under storage pressure or after a stretch of disuse -- which is
// PLAN 13.7, the installed app left alone for a week. Persistent storage is
// the documented way to opt out of that.
// ---------------------------------------------------------------------------

export interface StorageReport {
  /** False when the browser has no Storage API at all; the rest is then unknown. */
  supported: boolean;
  usage: number | null;
  quota: number | null;
  /** The browser has promised not to evict this origin. */
  persisted: boolean;
  /**
   * Whether asking is even possible. Safari does not implement persist(): it
   * grants durability to home-screen installs on its own, so a false here is
   * not the same as data being at risk, and the UI must not claim it is.
   */
  canRequestPersistence: boolean;
}

const UNSUPPORTED: StorageReport = {
  supported: false,
  usage: null,
  quota: null,
  persisted: false,
  canRequestPersistence: false,
};

export async function readStorage(): Promise<StorageReport> {
  const manager: StorageManager | undefined = navigator.storage;
  if (!manager || typeof manager.estimate !== 'function') return UNSUPPORTED;

  const estimate = await manager.estimate();
  const persisted =
    typeof manager.persisted === 'function' ? await manager.persisted() : false;

  return {
    supported: true,
    usage: estimate.usage ?? null,
    quota: estimate.quota ?? null,
    persisted,
    canRequestPersistence: typeof manager.persist === 'function',
  };
}

/**
 * Ask the browser to stop evicting us. Chrome decides from engagement
 * heuristics and usually says yes to an installed app; a refusal is not an
 * error worth showing anyone, so this reports the answer rather than throwing.
 */
export async function requestPersistence(): Promise<boolean> {
  const manager: StorageManager | undefined = navigator.storage;
  if (!manager || typeof manager.persist !== 'function') return false;

  try {
    if (typeof manager.persisted === 'function' && (await manager.persisted())) {
      return true;
    }
    return await manager.persist();
  } catch {
    return false;
  }
}
